import { useQuery } from "@tanstack/react-query";

export interface ProfileField {
  key: string;
  label: string;
  required: boolean;
  section: "overview" | "contact";
}

export const PROFILE_FIELDS: ProfileField[] = [
  // Agency Overview fields
  { key: "agencyName", label: "Agency Name", required: true, section: "overview" },
  { key: "agencyLicenseNumber", label: "License Number", required: false, section: "overview" },
  { key: "agencyEntityType", label: "Entity Type", required: false, section: "overview" },
  { key: "agencyEIN", label: "EIN", required: false, section: "overview" },
  { key: "sicCode", label: "SIC Code", required: false, section: "overview" },
  { key: "establishedDate", label: "Established Date", required: false, section: "overview" },
  // Contact Information fields
  { key: "phone", label: "Phone Number", required: false, section: "contact" },
  { key: "contactEmail", label: "Contact Email", required: false, section: "contact" },
  { key: "website", label: "Website", required: false, section: "contact" },
  { key: "agencyAddress1", label: "Address", required: false, section: "contact" },
  { key: "agencyCity", label: "City", required: false, section: "contact" },
  { key: "agencyState", label: "State", required: false, section: "contact" },
  { key: "agencyZipCode", label: "ZIP Code", required: false, section: "contact" },
];

export const OVERVIEW_FIELDS = PROFILE_FIELDS.filter(f => f.section === "overview");
export const CONTACT_FIELDS = PROFILE_FIELDS.filter(f => f.section === "contact");

export interface ProfileCompletionResult {
  isComplete: boolean;
  completionPercentage: number;
  missingFields: ProfileField[];
  missingRequiredFields: ProfileField[];
  completedFields: number;
  totalFields: number;
  profile: any;
  isLoading: boolean;
}

export function useProfileCompletion(): ProfileCompletionResult {
  const { data: profile, isLoading } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: () => fetch("/api/user/profile").then((r) => r.json()),
  });

  if (isLoading || !profile) {
    return {
      isComplete: true, // Assume complete while loading to avoid flashing
      completionPercentage: 100,
      missingFields: [],
      missingRequiredFields: [],
      completedFields: 0,
      totalFields: PROFILE_FIELDS.length,
      profile: null,
      isLoading,
    };
  }

  const missingFields: ProfileField[] = [];
  const missingRequiredFields: ProfileField[] = [];
  let completedCount = 0;

  for (const field of PROFILE_FIELDS) {
    const value = profile[field.key];
    const isEmpty = value === null || value === undefined ||
      (typeof value === "string" && value.trim() === "");

    if (isEmpty) {
      missingFields.push(field);
      if (field.required) {
        missingRequiredFields.push(field);
      }
    } else {
      completedCount++;
    }
  }

  const completionPercentage = Math.round(
    (completedCount / PROFILE_FIELDS.length) * 100
  );

  return {
    isComplete: missingFields.length === 0,
    completionPercentage,
    missingFields,
    missingRequiredFields,
    completedFields: completedCount,
    totalFields: PROFILE_FIELDS.length,
    profile,
    isLoading,
  };
}
